/*
  hj-pages.js: behavior for the composed page sections (static markup copied from the live Hydrogen store) and the
  blog/journal. Every handler below mirrors the live React component named in its comment; state is kept in the
  same attributes and inline styles the live component writes, so the markup's own CSS keeps working.

  Forms post to Shopify instead of the live store's private APIs:
    newsletter          -> /contact form_type=customer (contact[email], contact[tags]=newsletter)
    contact / warranty  -> /contact form_type=contact (contact[...] fields)
  Left out: the Google Maps store map, the etch builder app on /pages/customizer, order lookup (no public API).
*/
(function () {
  'use strict';

  var main = document.querySelector('main.hj-main') || document.getElementById('MainContent');
  if (!main) return;
  var path = location.pathname.replace(/\/$/, '');
  var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  /* ---------- typography and button styles (the live p() / J() helpers) ---------- */
  var FONT = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
  var TYPE = {
    eyebrow: ['10px', '600', '1.2', '0.16em', 'uppercase'],
    body: ['15px', '400', '1.7', '0', 'none'],
    'body-lg': ['1.125rem', '400', '1.7', '0', 'none'],
    'product-title': ['15px', '600', '1.3', '-0.01em', 'none'],
    'fine-print': ['11px', '400', '1.5', '0.04em', 'none'],
    button: ['11px', '600', '1', '0.16em', 'uppercase']
  };
  function typo(name) {
    var t = TYPE[name];
    return 'font-family:var(--type-' + name + '-family, ' + FONT + ');font-size:var(--type-' + name + '-size, ' + t[0] +
      ');font-weight:var(--type-' + name + '-weight, ' + t[1] + ');line-height:var(--type-' + name + '-line-height, ' + t[2] +
      ');letter-spacing:var(--type-' + name + '-letter-spacing, ' + t[3] + ');text-transform:var(--type-' + name + '-transform, ' + t[4] + ');';
  }
  function btn(variant) {
    var c = variant === 'secondary'
      ? 'background-color:var(--color-button-secondary-bg, var(--color-surface, #ffffff));color:var(--color-button-secondary-text, var(--color-text, #1a1a1a));border:var(--button-border-width, 1px) solid var(--color-button-secondary-border, var(--color-text, #1a1a1a));'
      : 'background-color:var(--color-button-bg, var(--color-text, #1a1a1a));color:var(--color-button-text, var(--color-on-primary, #ffffff));border:var(--button-border-width, 1px) solid var(--color-button-border, var(--color-text, #1a1a1a));';
    return typo('button') + 'display:inline-flex;align-items:center;justify-content:center;' + c +
      'padding-block:var(--button-padding-block, 14px);padding-inline:var(--button-padding-inline, 32px);min-height:var(--button-min-height, auto);border-radius:var(--radius-button, 12px);text-decoration:none;cursor:pointer';
  }

  /* ---------- Shopify form posts ---------- */
  // Posts like the theme's {% form %} would; resolves 'ok', 'challenge' (bot check, needs a real navigation) or 'error'.
  function shopifyPost(fields) {
    var fd = new FormData();
    fd.append('utf8', '✓');
    Object.keys(fields).forEach(function (k) { fd.append(k, fields[k]); });
    return fetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'contact', {
      method: 'POST', body: fd, credentials: 'same-origin', headers: { Accept: 'text/html' }
    }).then(function (r) {
      if (/\/challenge/.test(r.url)) return 'challenge';
      if (r.ok && /(customer|contact)_posted=true/.test(r.url)) return 'ok';
      return 'error';
    }).catch(function () { return 'error'; });
  }
  // Same post as a real navigation (Shopify's bot check page then sends the visitor back).
  function shopifyNavigate(fields) {
    var f = document.createElement('form');
    f.method = 'post'; f.action = '/contact'; f.hidden = true;
    fields.utf8 = '✓';
    Object.keys(fields).forEach(function (k) { var i = document.createElement('input'); i.type = 'hidden'; i.name = k; i.value = fields[k]; f.appendChild(i); });
    document.body.appendChild(f); f.submit();
  }

  /* ---------- FAQ / accordion (faq, accordion): one item open at a time ---------- */
  // Item = <div id="slug"><button aria-expanded>...</button><div style="display:grid;grid-template-rows:..."></div></div>
  function faqPanel(button) {
    var p = button.nextElementSibling;
    return p && /grid-template-rows/.test(p.getAttribute('style') || '') ? p : null;
  }
  function faqSet(button, open) {
    var panel = faqPanel(button);
    if (!panel) return;
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.style.gridTemplateRows = open ? '1fr' : '0fr';
    panel.style.opacity = open ? '1' : '0';
    var svg = button.querySelector('svg');
    if (svg) svg.style.transform = open ? 'rotate(180deg)' : 'rotate(0deg)';
    else {
      // accordion: plus icon whose vertical bar turns and fades
      var bars = button.querySelectorAll('span[aria-hidden] > span');
      if (bars[1]) { bars[1].style.opacity = open ? '0' : '1'; bars[1].style.transform = open ? 'translateX(-50%) rotate(90deg)' : 'translateX(-50%)'; }
    }
  }
  function faqItems(list) {
    return Array.prototype.filter.call(list.children, function (el) {
      var b = el.firstElementChild;
      return b && b.tagName === 'BUTTON' && b.hasAttribute('aria-expanded') && faqPanel(b);
    });
  }
  main.addEventListener('click', function (e) {
    var b = e.target.closest('button[aria-expanded]');
    if (!b || !main.contains(b) || !faqPanel(b)) return;
    var item = b.parentElement, list = item.parentElement;
    var wasOpen = b.getAttribute('aria-expanded') === 'true';
    faqItems(list).forEach(function (it) { faqSet(it.firstElementChild, false); });
    if (!wasOpen) faqSet(b, true);
  });
  // /pages/help-x#question-slug opens that question (live faq does this on load and on hashchange).
  function faqFromHash() {
    var id = '';
    try { id = decodeURIComponent(location.hash.replace(/^#/, '')); } catch (err) { return; }
    if (!id) return;
    var item = document.getElementById(id);
    var b = item && item.firstElementChild;
    if (!b || b.tagName !== 'BUTTON' || !faqPanel(b) || !main.contains(item)) return;
    faqItems(item.parentElement).forEach(function (it) { faqSet(it.firstElementChild, false); });
    faqSet(b, true);
  }
  faqFromHash();
  window.addEventListener('hashchange', faqFromHash);

  /* ---------- FAQ with hidden panels (hjLoyaltyFAQ, hjRecallFAQ): one open at a time ---------- */
  main.addEventListener('click', function (e) {
    var b = e.target.closest('button[aria-controls][aria-expanded]');
    if (!b || !main.contains(b)) return;
    var item = b.closest('[data-open]');
    var panel = document.getElementById(b.getAttribute('aria-controls'));
    if (!item || !panel) return;
    var wasOpen = b.getAttribute('aria-expanded') === 'true';
    $$('[data-open]', item.parentElement).forEach(function (it) {
      if (it.parentElement !== item.parentElement) return;
      var ib = it.querySelector('button[aria-controls]');
      var ip = ib && document.getElementById(ib.getAttribute('aria-controls'));
      it.setAttribute('data-open', 'false');
      if (ib) ib.setAttribute('aria-expanded', 'false');
      if (ip) ip.hidden = true;
    });
    if (!wasOpen) { item.setAttribute('data-open', 'true'); b.setAttribute('aria-expanded', 'true'); panel.hidden = false; }
  });

  /* ---------- help centre search (aboutHero search box filters hjTopicCards and faq) ---------- */
  (function helpSearch() {
    var form = $('form[role="search"]', main);
    var input = form && $('input[type="search"]', form);
    if (!input) return;
    var note = $('p[aria-live]', form);
    form.addEventListener('submit', function (e) { e.preventDefault(); });
    var cards = $$('.hj-tc-card', main);
    var faqLists = $$('section', main).map(function (s) {
      var b = $('button[aria-expanded]', s);
      return b && faqPanel(b) ? { section: s, list: b.parentElement.parentElement } : null;
    }).filter(Boolean);
    function run() {
      var shown = input.value.trim(), q = shown.toLowerCase(), total = 0;
      if (cards.length) {
        var n = 0;
        cards.forEach(function (c) {
          var t = ($('.hj-tc-title', c) || {}).textContent || '';
          var d = ($('.hj-tc-desc', c) || {}).textContent || '';
          var ok = !q || (t + ' ' + d).toLowerCase().indexOf(q) >= 0;
          c.hidden = !ok; if (ok) n++;
        });
        var sec = cards[0].closest('section');
        if (sec) sec.hidden = !!q && n === 0;
        total += n;
      }
      faqLists.forEach(function (f) {
        var items = faqItems(f.list), visible = [];
        items.forEach(function (it) {
          var ok = !q || it.textContent.toLowerCase().indexOf(q) >= 0;
          it.hidden = !ok; if (ok) visible.push(it);
        });
        items.forEach(function (it) { it.style.borderBottom = it === visible[visible.length - 1] ? 'none' : '1px solid var(--color-muted, #d4d4d4)'; });
        f.section.hidden = !!q && visible.length === 0;
        total += visible.length;
      });
      if (note) note.textContent = q && total === 0 ? 'No results for "' + shown + '".' : '';
    }
    input.addEventListener('input', run);
  })();

  /* ---------- countdown banners (countdownBanner) on the drop landing pages ---------- */
  var COUNTDOWNS = {
    '/pages/fall-seasonal-2026': { end: '2026-09-05T09:00:00-07:00', eyebrow: 'Launching In', msg: 'Rylee X HydroJug is Live', msgType: 'body-lg', cta: ['Shop New Core', '/collections', 'primary'] },
    '/pages/halloween-2026': { end: '2026-09-19T09:00:00-07:00', eyebrow: 'Launching In', msg: 'Shop Halloween Collection', msgType: 'body', cta: ['Shop New Releases', '/collections/new-release', 'secondary'] },
    '/pages/new-core': { end: '2026-08-15T09:00:00-07:00', eyebrow: 'Launching In', msg: 'New Core Colors are live.', msgType: 'body-lg', cta: ['Shop New Core', '/collections', 'primary'] },
    '/pages/rylee-arnold': { end: '2026-08-29T09:00:00-07:00', eyebrow: '', msg: 'Rylee X HydroJug is Live', msgType: 'body-lg', cta: ['Shop New Core', '/collections', 'primary'] }
  };
  (function countdown() {
    var cfg = COUNTDOWNS[path];
    if (!cfg) return;
    var section = $$('section', main).filter(function (s) { return /^width:100%;padding:2\.5rem 0;background:/.test(s.getAttribute('style') || ''); })[0];
    var box = section && $('.sb-container', section);
    if (!box) return;
    var end = new Date(cfg.end).getTime();
    function left() {
      var r = end - Date.now();
      return r <= 0 ? null : { days: Math.floor(r / 864e5), hours: Math.floor(r % 864e5 / 36e5), minutes: Math.floor(r % 36e5 / 6e4), seconds: Math.floor(r % 6e4 / 1e3) };
    }
    function expired() {
      box.setAttribute('style', 'text-align:center');
      box.innerHTML =
        (cfg.eyebrow ? '<p style="' + typo('eyebrow') + 'margin:0 0 12px;color:var(--color-on-primary, #fff);opacity:0.72">' + esc(cfg.eyebrow) + '</p>' : '') +
        (cfg.msg ? '<p style="' + typo(cfg.msgType) + 'margin:0;color:var(--color-on-primary, #fff)">' + esc(cfg.msg) + '</p>' : '') +
        (cfg.cta ? '<div style="margin-top:' + (cfg.msg || cfg.eyebrow ? 20 : 0) + 'px"><a href="' + esc(cfg.cta[1]) + '" style="' + btn(cfg.cta[2]) + '">' + esc(cfg.cta[0]) + '</a></div>' : '');
    }
    var tile = function (v, label) {
      return '<div style="display:flex;flex-direction:column;align-items:center;flex:0 0 auto"><div style="display:flex;align-items:center;justify-content:center;min-width:var(--sb-countdown-tile-width, clamp(3.5rem, 5.6vw, 5rem));min-height:var(--sb-countdown-tile-height, clamp(4rem, 6.2vw, 5.5rem));padding:0 0.6rem;box-sizing:border-box;border-radius:var(--sb-countdown-tile-radius, 12px);background:var(--sb-countdown-tile-bg, rgba(255, 255, 255, 0.08))"><span style="font-family:var(--font-display, var(--font-heading, sans-serif));font-size:var(--sb-countdown-digit-size, clamp(1.75rem, 3.4vw, 2.75rem));font-weight:800;line-height:1;color:var(--color-on-primary, #fff);font-variant-numeric:tabular-nums">' +
        String(v).padStart(2, '0') + '</span></div><span style="margin-top:var(--sb-countdown-label-gap, 0.5rem);font-family:var(--font-body, sans-serif);font-size:var(--sb-countdown-label-size, 0.625rem);font-weight:500;text-transform:uppercase;letter-spacing:0.12em;color:var(--color-on-primary, #fff);opacity:0.55">' + label + '</span></div>';
    };
    var dot = '<span style="display:block;width:var(--sb-countdown-sep-dot, 4px);height:var(--sb-countdown-sep-dot, 4px);border-radius:50%;background:currentColor;opacity:0.38"></span>';
    var sep = '<span aria-hidden="true" style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:var(--sb-countdown-sep-gap, 0.4rem);min-height:var(--sb-countdown-tile-height, clamp(4rem, 6.2vw, 5.5rem));flex:0 0 auto;color:var(--color-on-primary, #fff)">' + dot + dot + '</span>';
    var inline = /display:flex/.test(box.getAttribute('style') || '');
    var clock = null;
    function render(t) {
      if (!clock) {
        clock = document.createElement('div');
        if (inline) box.insertBefore(clock, box.lastElementChild && box.lastElementChild.tagName === 'A' ? box.lastElementChild : null);
        else {
          clock.style.marginTop = 'var(--sb-countdown-clock-gap, 2.5rem)';
          var cta = box.querySelector(':scope > div:last-child');
          box.insertBefore(clock, cta && cta.querySelector('a') ? cta : null);
        }
      }
      clock.innerHTML = '<div style="display:flex;align-items:flex-start;justify-content:center;gap:var(--sb-countdown-gap, clamp(0.5rem, 1.6vw, 1.25rem))">' +
        tile(t.days, 'Days') + sep + tile(t.hours, 'Hours') + sep + tile(t.minutes, 'Mins') + sep + tile(t.seconds, 'Secs') + '</div>';
    }
    function tick() {
      var t = left();
      if (t) render(t);
      else { clearInterval(timer); expired(); }
    }
    var timer = setInterval(tick, 1000);
    tick();
  })();

  /* ---------- newsletter (newsletter section) ---------- */
  $$('.ac-newsletter form', main).forEach(function (form) {
    var section = form.closest('.ac-newsletter');
    var input = $('input[type="email"]', form), button = $('button[type="submit"]', form);
    if (!input || !button) return;
    var label = button.textContent;
    var bg = section.getAttribute('style') || '';
    var dark = /background:var\(--color-(text|primary)/.test(bg);
    var fg = dark ? 'var(--color-on-primary, #FFFFFF)' : 'var(--color-text, #1A1A1A)';
    var errorEl = null;
    function busy(on) {
      button.disabled = on; button.textContent = on ? 'Subscribing…' : label;
      button.style.cursor = on ? 'wait' : 'pointer'; button.style.opacity = on ? '0.7' : '1';
    }
    function error(msg) {
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.setAttribute('role', 'alert');
        errorEl.setAttribute('style', typo('fine-print') + 'color:' + fg + ';max-width:460px;margin:12px auto 0');
        form.parentNode.insertBefore(errorEl, form.nextSibling);
      }
      errorEl.textContent = msg;
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = input.value.trim();
      if (!email || button.disabled) return;
      busy(true);
      var fields = { form_type: 'customer', 'contact[email]': email, 'contact[tags]': 'newsletter' };
      shopifyPost(fields).then(function (res) {
        busy(false);
        if (res === 'challenge') return shopifyNavigate(fields);
        if (res !== 'ok') return error('We could not sign you up just now. Please try again later.');
        if (errorEl) { errorEl.remove(); errorEl = null; }
        var done = document.createElement('div');
        done.setAttribute('style', 'display:flex;align-items:center;justify-content:center;gap:8px;max-width:460px;margin:0 auto');
        done.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="' + (dark ? 'var(--color-accent, #ADA9A3)' : 'var(--color-text, #1A1A1A)') +
          '" stroke-width="2.5" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg><span style="' + typo('product-title') + 'color:' + fg + '">Thanks for subscribing!</span>';
        form.replaceWith(done);
      });
    });
  });

  /* ---------- simple contact forms (contact section: /pages/contact, /pages/data-sharing-opt-out) ---------- */
  // Live: <form action="mailto:..." method="get">. Here: Shopify's contact form, a normal post that returns with
  // ?contact_posted=true.
  $$('form[action^="mailto:"]', main).forEach(function (form) {
    form.setAttribute('action', '/contact#contact_form');
    form.setAttribute('method', 'post');
    form.setAttribute('accept-charset', 'UTF-8');
    var map = { name: 'contact[name]', email: 'contact[email]', phone: 'contact[phone]', message: 'contact[body]' };
    $$('[name]', form).forEach(function (el) { if (map[el.name]) el.name = map[el.name]; });
    [['form_type', 'contact'], ['utf8', '✓'], ['contact[Page]', document.title]].forEach(function (f) {
      var i = document.createElement('input'); i.type = 'hidden'; i.name = f[0]; i.value = f[1]; form.appendChild(i);
    });
    if (/[?&]contact_posted=true/.test(location.search)) {
      var done = document.createElement('p');
      done.setAttribute('role', 'status');
      done.setAttribute('style', typo('body') + 'margin:0;max-width:460px');
      done.textContent = 'Thanks for contacting us. We’ll get back to you as soon as possible.';
      form.replaceWith(done);
    }
  });

  /* ---------- contact form with request type and attachments (hjContactForm, /pages/contact-us) ---------- */
  $$('form.hj-cf2-form', main).forEach(function (form) {
    var section = form.closest('.hj-cf2'), inner = $('.hj-cf2-inner', section);
    var submit = $('.hj-cf2-submit', form), fileInput = $('input[type="file"]', form), drop = $('.hj-cf2-drop', form);
    var MAX_FILES = 3, MAX_BYTES = 5 * 1024 * 1024;
    var TYPES = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/heic': 'heic', 'image/heif': 'heif', 'application/pdf': 'pdf', 'application/msword': 'doc', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx' };
    var EXTS = Object.keys(TYPES).map(function (k) { return TYPES[k]; });
    var files = [], sending = false, errorEl = null, label = submit ? submit.textContent : 'Send message';
    function error(msg) {
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'hj-cf2-error'; errorEl.tabIndex = -1; errorEl.setAttribute('role', 'alert');
        form.insertBefore(errorEl, submit);
      }
      errorEl.textContent = msg; errorEl.focus();
    }
    function clearError() { if (errorEl) { errorEl.remove(); errorEl = null; } }
    function renderFiles() {
      var field = fileInput && fileInput.closest('.hj-cf2-field');
      if (!field) return;
      var ul = $('.hj-cf2-files', field);
      if (!files.length) { if (ul) ul.remove(); return; }
      if (!ul) { ul = document.createElement('ul'); ul.className = 'hj-cf2-files'; field.appendChild(ul); }
      ul.innerHTML = files.map(function (f, i) {
        return '<li class="hj-cf2-file"><span class="hj-cf2-filename">' + esc(f.name) + '</span><button class="hj-cf2-remove" type="button" data-i="' + i + '">Remove<span class="hj-cf2-sr"> ' + esc(f.name) + '</span></button></li>';
      }).join('');
    }
    function addFiles(list) {
      if (!list) return;
      for (var k = 0; k < list.length; k++) {
        var f = list[k];
        if (files.length >= MAX_FILES) { error('Please attach no more than ' + MAX_FILES + ' files.'); break; }
        if (f.size > MAX_BYTES) { error(f.name + ' is too large. Each file must be under 5 MB.'); continue; }
        var ext = (f.name.split('.').pop() || '').toLowerCase();
        if (!(f.type in TYPES) && EXTS.indexOf(ext) < 0) { error(f.name + ' is not an accepted file type (JPG, PNG, WEBP, HEIC, HEIF, PDF, DOC, DOCX).'); continue; }
        if (!files.some(function (x) { return x.name === f.name && x.size === f.size; })) files.push(f);
      }
      renderFiles();
      if (fileInput) fileInput.value = '';
    }
    if (fileInput) fileInput.addEventListener('change', function () { addFiles(fileInput.files); });
    form.addEventListener('click', function (e) {
      var r = e.target.closest('.hj-cf2-remove');
      if (r) { files.splice(+r.getAttribute('data-i'), 1); renderFiles(); }
    });
    if (drop) {
      var depth = 0;
      drop.addEventListener('dragenter', function (e) { e.preventDefault(); depth++; drop.classList.add('is-dragging'); });
      drop.addEventListener('dragover', function (e) { e.preventDefault(); });
      drop.addEventListener('dragleave', function () { depth = Math.max(0, depth - 1); if (!depth) drop.classList.remove('is-dragging'); });
      drop.addEventListener('drop', function (e) { e.preventDefault(); depth = 0; drop.classList.remove('is-dragging'); addFiles(e.dataTransfer && e.dataTransfer.files); });
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (sending) return;
      var fd = new FormData(form), v = function (k) { return String(fd.get(k) || '').trim(); };
      if (!v('name')) return error('Please enter your name.');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v('email'))) return error('Please enter a valid email address (e.g. you@example.com).');
      if (!v('requestType')) return error('Please choose what your message is about.');
      if (v('orderNumber') && !/^[A-Za-z0-9#-]{0,32}$/.test(v('orderNumber'))) return error('That order number does not look right.');
      if (!v('message')) return error('Please enter a message.');
      var select = $('select[name="requestType"]', form);
      var body = v('message');
      // Shopify's contact form carries no files: name them so the team can ask for them by email.
      if (files.length) body += '\n\nAttachments selected (not uploaded by this form, please request them by email): ' + files.map(function (f) { return f.name; }).join(', ');
      var fields = {
        form_type: 'contact',
        'contact[name]': v('name'),
        'contact[email]': v('email'),
        'contact[Request type]': select && select.selectedIndex > 0 ? select.options[select.selectedIndex].text : v('requestType'),
        'contact[Order number]': v('orderNumber'),
        'contact[body]': body
      };
      function done() {
        inner.innerHTML = '<div class="hj-cf2-done" tabindex="-1" role="status"><h2 class="hj-cf2-doneh">Message received</h2></div>';
        $('.hj-cf2-done', inner).focus();
      }
      if (v('_honey')) return done();
      sending = true; clearError();
      if (submit) { submit.textContent = 'Sending…'; submit.setAttribute('aria-disabled', 'true'); }
      shopifyPost(fields).then(function (res) {
        sending = false;
        if (submit) { submit.textContent = label; submit.removeAttribute('aria-disabled'); }
        if (res === 'challenge') return shopifyNavigate(fields);
        if (res === 'ok') return done();
        error('We could not send that just now. Please try again, or email service@thehydrojug.com.');
      });
    });
  });

  /* ---------- shipment tracker (hjShipmentTracker) ---------- */
  // The live lookup is a private API over the Admin API; a theme cannot read orders without a signed-in customer,
  // so a valid lookup points to the customer account, where every order shows its tracking.
  $$('form.hj-tr-form', main).forEach(function (form) {
    var order = $('#hj-tr-order', form), button = $('button[type="submit"]', form), errorEl = null;
    var q = new URLSearchParams(location.search), pre = q.get('order-number') || q.get('order') || '';
    if (order && pre) order.value = pre.replace(/^#+/, '');
    function message(html) {
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'hj-tr-err'; errorEl.setAttribute('role', 'alert'); errorEl.setAttribute('aria-live', 'assertive');
        form.appendChild(errorEl);
      }
      errorEl.innerHTML = html;
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var honey = $('input[name="_honey"]', form);
      if (honey && honey.value) return;
      var n = order ? order.value.trim().replace(/^#+/, '') : '';
      if (!n) return message('Enter the order number from your confirmation email.');
      message('Sign in to <a href="/account" style="color:inherit;text-decoration:underline">your account</a> to see live shipping updates for order #' + esc(n) + '.');
      if (button) button.blur();
    });
  });

  /* ---------- store locator (hjStoreLocator) ---------- */
  $$('section.hjsl', main).forEach(function (S) {
    var I = $('[data-hjsl-search]', S), E = $('[data-hjsl-filter-type]', S), O = $('[data-hjsl-clear]', S);
    var K = $('[data-hjsl-count]', S), ne = $('[data-hjsl-empty]', S);
    var views = $$('[data-hjsl-view]', S).filter(function (b) { return b.tagName === 'BUTTON'; });
    var stores = $$('[data-hjsl-store]', S);
    function filter() {
      var q = ((I && I.value) || '').trim().toLowerCase();
      var type = E && E.value && E.value !== 'All' ? E.value : '';
      var n = 0;
      stores.forEach(function (st) {
        var item = st.closest('.hjsl-item');
        var ok = (!q || (st.getAttribute('data-hjsl-text') || '').indexOf(q) >= 0) && (!type || st.getAttribute('data-hjsl-type') === type);
        if (item) item.hidden = !ok;
        if (ok) { n++; var num = $('[data-hjsl-num]', st); if (num) num.textContent = String(n); }
      });
      if (K) K.textContent = n + (n === 1 ? ' store' : ' stores');
      if (ne) ne.hidden = n !== 0;
      if (O) O.hidden = !(q || type);
      if (E) E.setAttribute('data-active', type ? 'true' : 'false');
    }
    if (I) I.addEventListener('input', filter);
    if (E) E.addEventListener('change', filter);
    if (O) O.addEventListener('click', function () { if (I) I.value = ''; if (E) E.value = 'All'; filter(); });
    views.forEach(function (b) {
      b.addEventListener('click', function () {
        S.setAttribute('data-hjsl-view', b.getAttribute('data-hjsl-view') || 'list');
        views.forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
      });
    });
    filter();
    // Map slot: the live map is Google Maps on HydroJug's key. Shown the way the live page shows a map that cannot
    // load (the "Use my current location" button only pans that map, so it has nothing to do here either).
    var slot = $('[data-hjsl-pane="map"] [data-sb-client-only]', S);
    if (slot) {
      var map = document.createElement('div');
      map.className = 'hjsl-map';
      map.style.height = (S.style.getPropertyValue('--hjsl-h') || '600px').trim();
      map.innerHTML = '<style>.hjsl-map{position:relative;width:100%;height:100%;min-height:320px}.hjsl-map__canvas{position:absolute;inset:0;width:100%;height:100%}' +
        '.hjsl-map__msg{position:absolute;inset:0;z-index:3;display:flex;align-items:center;justify-content:center;padding:24px;background:var(--color-custom-stone, #E8E6E1)}' +
        '.hjsl-map__msgbox{max-width:460px;padding:16px 20px;border:1px solid var(--color-border, #E8E6E1);border-radius:var(--radius-card, 8px);background:var(--color-white, #FFFFFF);font-family:var(--font-body, sans-serif);font-size:13px;line-height:1.6;color:var(--color-secondary, #333333);text-align:center}</style>' +
        '<div class="hjsl-map__canvas" role="application" aria-label="Map of retail partners"></div><div class="hjsl-map__msg"><p class="hjsl-map__msgbox" role="status">The map is unavailable right now. The full list of retail partners is below.</p></div>';
      slot.replaceWith(map);
    }
  });

  /* ---------- referral link copy (hjLoyaltyReferral) ---------- */
  $$('.hjlrf-copy', main).forEach(function (b) {
    var label = b.textContent, timer = null;
    var url = $('.hjlrf-url', b.parentElement), sr = $('.hjlrf-sr', b.closest('.hjlrf-panel') || main);
    function copied() {
      b.textContent = 'COPIED'; if (sr) sr.textContent = 'COPIED';
      clearTimeout(timer);
      timer = setTimeout(function () { b.textContent = label; if (sr) sr.textContent = ''; }, 2000);
    }
    function fallback(text) {
      var t = document.createElement('textarea'); t.value = text; t.setAttribute('readonly', ''); t.style.position = 'fixed'; t.style.opacity = '0';
      document.body.appendChild(t); t.select();
      var ok = false; try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
      t.remove(); return ok;
    }
    b.addEventListener('click', function () {
      var text = url ? url.textContent.trim() : '';
      if (!text) return;
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(copied, function () { if (fallback(text)) copied(); });
      else if (fallback(text)) copied();
    });
  });

  /* ---------- home slideshow (slideshow): dots, autoplay every 15 s, pause on hover ---------- */
  $$('button[aria-label="Go to slide 1"]', main).forEach(function (first) {
    var root = first.closest('section');
    if (!root || root.hasAttribute('data-hj-slideshow')) return;
    root.setAttribute('data-hj-slideshow', '');
    var slides = $$(':scope > div > div', root).filter(function (d) { return /transition:\s*opacity/.test(d.getAttribute('style') || ''); });
    if (slides.length < 2) return;
    var current = Math.max(0, slides.findIndex(function (s) { return s.getAttribute('aria-hidden') === 'false'; }));
    var hover = false, timer = null, CYCLE = 15000;
    function show(i) {
      current = (i + slides.length) % slides.length;
      slides.forEach(function (s, k) {
        var on = k === current;
        s.style.opacity = on ? '1' : '0'; s.style.zIndex = on ? '10' : '0'; s.style.pointerEvents = on ? 'auto' : 'none';
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
        $$('video', s).forEach(function (v) {
          v.preload = on ? 'auto' : 'metadata';
          if (on) { v.autoplay = true; v.muted = true; var p = v.play(); if (p && p.catch) p.catch(function () {}); }
          else { v.autoplay = false; v.pause(); try { v.currentTime = 0; } catch (err) { /* not loaded yet */ } }
        });
        $$('button[aria-label^="Go to slide"]', s).forEach(function (d, j) {
          var active = j === current;
          d.style.width = active ? '24px' : '8px'; d.style.opacity = active ? '1' : '0.3';
          if (active) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
          d.tabIndex = on ? 0 : -1;
        });
      });
    }
    function schedule() {
      clearInterval(timer); timer = null;
      if (hover || reducedMotion.matches) return;
      timer = setInterval(function () { show(current + 1); }, CYCLE);
    }
    root.addEventListener('click', function (e) {
      var d = e.target.closest('button[aria-label^="Go to slide"]');
      if (!d) return;
      show(parseInt(d.getAttribute('aria-label').replace(/\D+/g, ''), 10) - 1);
      schedule();
    });
    root.addEventListener('mouseenter', function () { hover = true; schedule(); });
    root.addEventListener('mouseleave', function () { hover = false; schedule(); });
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', schedule);
    schedule();
  });

  /* ---------- category cards row (categoryCards): arrow keys / Home / End scroll the row ---------- */
  $$('.ac-catcards__row', main).forEach(function (row) {
    function step() {
      var first = row.firstElementChild;
      if (!first) return Math.round(row.clientWidth * 0.8);
      return first.getBoundingClientRect().width + (parseFloat(getComputedStyle(row).columnGap) || 0);
    }
    row.addEventListener('keydown', function (e) {
      var behavior = reducedMotion.matches ? 'auto' : 'smooth';
      if (e.key === 'ArrowRight') { e.preventDefault(); row.scrollBy({ left: step(), behavior: behavior }); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); row.scrollBy({ left: -step(), behavior: behavior }); }
      else if (e.key === 'Home') { e.preventDefault(); row.scrollTo({ left: 0, behavior: behavior }); }
      else if (e.key === 'End') { e.preventDefault(); row.scrollTo({ left: row.scrollWidth, behavior: behavior }); }
    });
  });

  /* ---------- legal documents (hjLegalDocs): in-page links, sticky nav height, current document ---------- */
  $$('section.hjrb', main).forEach(function (s) {
    var strip = $('[data-hjld-navstrip]', s);
    s.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a || !s.contains(a)) return;
      var href = a.getAttribute('href') || '', id = href.slice(1);
      if (!id) return;
      var el = document.getElementById(id);
      if (!el) { try { el = document.getElementById(decodeURIComponent(id)); } catch (err) { el = null; } }
      if (!el) return;
      e.preventDefault();
      if (history.replaceState) history.replaceState(null, '', href);
      el.scrollIntoView({ block: 'start' });
    });
    if (!strip) return;
    function navHeight() { var h = Math.round(strip.getBoundingClientRect().height); if (h > 0) s.style.setProperty('--hjld-nav-h', h + 'px'); }
    navHeight();
    if (window.ResizeObserver) new ResizeObserver(navHeight).observe(strip); else window.addEventListener('resize', navHeight);
    var navs = {};
    $$('[data-hjld-nav]', s).forEach(function (n) { navs[n.getAttribute('data-hjld-nav')] = n; });
    var docs = $$('[data-hjld-doc]', s);
    if (!docs.length) return;
    var currentDoc = '', queued = false;
    function spy() {
      queued = false;
      var cs = getComputedStyle(s);
      var offset = (parseFloat(cs.getPropertyValue('--sb-anchor-offset')) || 96) + (parseFloat(cs.getPropertyValue('--hjld-nav-h')) || 88) + 24;
      var id = '';
      docs.forEach(function (d) { var k = d.getAttribute('data-hjld-doc') || ''; if (k && d.getBoundingClientRect().top <= offset) id = k; });
      if (!id) id = docs[0].getAttribute('data-hjld-doc') || '';
      if (!id || id === currentDoc) return;
      currentDoc = id;
      Object.keys(navs).forEach(function (k) { if (k === id) navs[k].setAttribute('aria-current', 'true'); else navs[k].removeAttribute('aria-current'); });
    }
    function queue() { if (!queued) { queued = true; requestAnimationFrame(spy); } }
    spy();
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
  });

  /* ---------- customizer design gallery (hjDesignGallery): category chips over all 431 preset designs ---------- */
  (function designGallery() {
    var chips = $$('.hjdg-chip', main);
    var grid = $('.hjdg-grid', main);
    if (!chips.length || !grid) return;
    var sr = $('.hjdg-sr', main), more = $('.hjdg-more', main);
    var data = null, loading = null, active = '';
    var src = (document.currentScript && document.currentScript.src) || ($('script[src*="hj-pages.js"]') || {}).src || '';
    function load() {
      if (data) return Promise.resolve(data);
      if (!loading) loading = fetch(src.replace(/hj-pages\.js(\?.*)?$/, 'hj-pages-designs.json')).then(function (r) { return r.json(); }).then(function (d) { data = d; return d; });
      return loading;
    }
    function tile(d) {
      var img = d[2] + (d[2].indexOf('?') >= 0 ? '&' : '?') + 'width=400';
      return '<li class="hjdg-tile"><a href="' + location.pathname + '?preset=' + encodeURIComponent(d[0]) + '&amp;customize=preset#customizer-builder" class="hjdg-link"><span class="hjdg-media"><img src="' +
        esc(img) + '" alt="' + esc(d[1]) + '" width="200" height="200" decoding="async" loading="lazy"/></span><span class="hjdg-name">' + esc(d[1]) + '</span></a></li>';
    }
    // "All": round-robin across categories, 24 designs (12 on phones), like the live gallery.
    function interleave(list, limit) {
      var by = {}, order = [], out = [];
      list.forEach(function (d) { if (!by[d[3]]) { by[d[3]] = []; order.push(d[3]); } by[d[3]].push(d); });
      for (var i = 0; out.length < limit; i++) {
        var any = false;
        for (var k = 0; k < order.length && out.length < limit; k++) { var d = by[order[k]][i]; if (d) { out.push(d); any = true; } }
        if (!any) break;
      }
      return out;
    }
    var phone = window.matchMedia('(max-width: 600px)');
    function render() {
      var list = active ? data.designs.filter(function (d) { return d[3] === active; }) : interleave(data.designs, phone.matches ? 12 : 24);
      grid.innerHTML = list.map(tile).join('');
      if (sr) sr.textContent = list.length + ' design' + (list.length === 1 ? '' : 's') + ' shown';
      if (more) {
        more.hidden = !(!active && data.designs.length > list.length);
        more.textContent = 'Showing ' + list.length + ' of ' + data.designs.length + ' designs — pick a category to see all of it';
      }
    }
    chips.forEach(function (c, i) {
      c.addEventListener('click', function () {
        var slug = '';
        if (i > 0) {
          var label = c.textContent.trim().toLowerCase();
          var hit = data && data.categories.filter(function (x) { return x[1].toLowerCase() === label; })[0];
          slug = hit ? hit[0] : label.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        }
        active = slug;
        chips.forEach(function (x) { var on = x === c; x.className = on ? 'hjdg-chip hjdg-chip--on' : 'hjdg-chip'; x.setAttribute('aria-pressed', String(on)); });
        load().then(function () {
          if (i > 0 && !data.categories.some(function (x) { return x[0] === active; })) {
            var label = c.textContent.trim().toLowerCase();
            var hit = data.categories.filter(function (x) { return x[1].toLowerCase() === label; })[0];
            if (hit) active = hit[0];
          }
          render();
        }).catch(function () {});
      });
    });
    if (phone.matches) load().then(render).catch(function () {});
    if (phone.addEventListener) phone.addEventListener('change', function () { if (data && !active) render(); });
  })();

  /* ---------- journal (blogGrid, blog template): category chips ---------- */
  (function journal() {
    var chips = $$('.blg-filters .blg-chip', main);
    var cards = $$('.blg-grid .blg-card', main);
    if (!chips.length || !cards.length) return;
    function apply(label) {
      var first = true;
      cards.forEach(function (c) {
        var ok = label === 'All' || c.getAttribute('data-hj-cat') === label;
        c.hidden = !ok;
        if (ok) { var w = $('.blg-img-wrap', c); if (w) w.style.height = first ? '400px' : '340px'; first = false; }
      });
      chips.forEach(function (x) { x.setAttribute('aria-pressed', String(x.textContent.trim() === label)); });
    }
    chips.forEach(function (c) { c.addEventListener('click', function () { apply(c.textContent.trim()); }); });
  })();
})();
